import { addDays, endOfDay, startOfDay } from "date-fns";

/**
 * Task 0038 (owner, 2026-09-30): one shared period model for every partner
 * orders/earnings surface — tenant revenue list + hotel detail, hotel
 * `/guesteria-partners/:id`, Guesteria `/partners/revenue` + orders, and
 * the hotel `/analytics` Guesteria Partners tab. Orders are filtered by
 * service date (`travelDate`, task 0033) — future ("Expected") bookings
 * must stay reachable, so "Custom" allows future dates and none of these
 * presets clamp to "today".
 */
export type PartnerRevenuePresetId =
  | "thisMonth"
  | "next30Days"
  | "lastMonth"
  | "last3Months"
  | "thisYear";

export type PartnerRevenuePeriodId = PartnerRevenuePresetId | "custom";

export const PARTNER_REVENUE_PRESETS: PartnerRevenuePresetId[] = [
  "thisMonth",
  "next30Days",
  "lastMonth",
  "last3Months",
  "thisYear",
];

/** "This month" is the default preset (owner, 2026-09-30). */
export const DEFAULT_PARTNER_REVENUE_PRESET: PartnerRevenuePresetId =
  "thisMonth";

export interface PartnerRevenueDateRange {
  from: Date;
  to: Date;
}

function startOfMonth(date: Date, monthOffset = 0): Date {
  return startOfDay(
    new Date(date.getFullYear(), date.getMonth() + monthOffset, 1),
  );
}

/** Day 0 of `month + offset + 1` is the last day of `month + offset`. */
function endOfMonth(date: Date, monthOffset = 0): Date {
  return endOfDay(
    new Date(date.getFullYear(), date.getMonth() + monthOffset + 1, 0),
  );
}

function startOfYear(date: Date): Date {
  return startOfDay(new Date(date.getFullYear(), 0, 1));
}

function endOfYear(date: Date): Date {
  return endOfDay(new Date(date.getFullYear(), 11, 31));
}

export function getPartnerRevenuePresetRange(
  preset: PartnerRevenuePresetId,
  referenceDate: Date = new Date(),
): PartnerRevenueDateRange {
  switch (preset) {
    case "next30Days":
      return {
        from: startOfDay(referenceDate),
        to: endOfDay(addDays(referenceDate, 30)),
      };
    case "lastMonth":
      return {
        from: startOfMonth(referenceDate, -1),
        to: endOfMonth(referenceDate, -1),
      };
    case "last3Months":
      return {
        from: startOfMonth(referenceDate, -2),
        to: endOfMonth(referenceDate),
      };
    case "thisYear":
      return {
        from: startOfYear(referenceDate),
        to: endOfYear(referenceDate),
      };
    case "thisMonth":
    default:
      return {
        from: startOfMonth(referenceDate),
        to: endOfMonth(referenceDate),
      };
  }
}

/** The default range — "This month". */
export function getDefaultPartnerRevenueRange(
  referenceDate: Date = new Date(),
): PartnerRevenueDateRange {
  return getPartnerRevenuePresetRange(
    DEFAULT_PARTNER_REVENUE_PRESET,
    referenceDate,
  );
}

// --- URL (de)serialization — YYYY-MM-DD, timezone-free on purpose (the
// resolved range always applies startOfDay/endOfDay in local time).

export function formatPartnerRevenueDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parsePartnerRevenueDateParam(
  value: string | null,
): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isPartnerRevenuePresetId(
  value: string | null,
): value is PartnerRevenuePresetId {
  return !!value && (PARTNER_REVENUE_PRESETS as string[]).includes(value);
}
